import { useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Link } from '@inertiajs/react';
import { route } from '../../utils/route';
import type { ProductListing, ProductFormat } from '../../types/products';
import { FormatSelector } from './FormatSelector';
import { QuantityInput } from './QuantityInput';
import type { ProductCardHandle } from '../../hooks/useGridKeyboardNav';

interface ProductCardProps {
  product: ProductListing;
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
  isFocused?: boolean;
  onInnerInputFocusChange?: (focused: boolean) => void;
}

function formatPrice(price: number): string {
  return price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const ProductCard = forwardRef<ProductCardHandle, ProductCardProps>(
  function ProductCard({ product, onAddToCart, isFocused, onInnerInputFocusChange }, ref) {
    const initialFormat = product.default_format ?? product.formats[0] ?? null;
    const [selectedFormat, setSelectedFormat] = useState<ProductFormat | null>(initialFormat);
    const [quantity, setQuantity] = useState(0);
    const [isAdding, setIsAdding] = useState(false);
    const [added, setAdded] = useState(false);

    const subtotal = useMemo(() => {
      if (!selectedFormat || quantity === 0) return 0;
      return selectedFormat.discounted_price * quantity;
    }, [selectedFormat, quantity]);

    const handleAddToCart = useCallback(async () => {
      if (!selectedFormat || quantity === 0) return;
      setIsAdding(true);
      try {
        await onAddToCart(product.id, selectedFormat.id, quantity);
        setAdded(true);
        setQuantity(0);
        setTimeout(() => setAdded(false), 2000);
      } catch {
        // error handled upstream
      } finally {
        setIsAdding(false);
      }
    }, [selectedFormat, quantity, product.id, onAddToCart]);

    useImperativeHandle(ref, () => ({
      cycleFormat() {
        if (product.formats.length <= 1) return;
        const currentIdx = product.formats.findIndex(f => f.id === selectedFormat?.id);
        const nextIdx = (currentIdx + 1) % product.formats.length;
        setSelectedFormat(product.formats[nextIdx]);
      },
      incrementQuantity() {
        setQuantity(q => Math.min(q + 1, 999));
      },
      decrementQuantity() {
        setQuantity(q => Math.max(q - 1, 0));
      },
      addToCart() {
        handleAddToCart();
      },
    }), [product.formats, selectedFormat, handleAddToCart]);

    const detailUrl = route('product.show', { slug: product.slug });

    return (
      <div className={`relative flex flex-col rounded-lg bg-white p-3 shadow-sm transition-shadow hover:shadow-md ${
        isFocused
          ? 'ring-2 ring-green-500 ring-offset-1 shadow-md'
          : product.is_featured
            ? 'border-2 border-green-500'
            : 'border border-gray-200'
      }`}>
        {/* Featured badge */}
        {product.is_featured && (
          <div className="absolute -top-px -left-px">
            <span className="inline-block rounded-tl-lg rounded-br-lg bg-green-500 px-3 py-1 text-xs font-bold text-white">
              Destacado
            </span>
          </div>
        )}

        {/* Image */}
        <Link href={detailUrl} className="mx-auto mb-2 block">
          {product.has_image ? (
            <img
              src={product.webp_image_url}
              alt={`${product.title} - ${product.category_name} - Abasto Virtual Córdoba`}
              title={`${product.title} - Delivery en Córdoba`}
              loading="lazy"
              width={150}
              height={150}
              className="aspect-square w-[150px] object-contain"
            />
          ) : (
            <div className="flex aspect-square w-[150px] items-center justify-center rounded bg-gray-100">
              <i className="fa fa-leaf text-3xl text-gray-300" />
            </div>
          )}
        </Link>

        {/* Name */}
        <Link href={detailUrl} className="mb-2 text-sm font-medium leading-tight text-gray-800 hover:text-green-700 no-underline line-clamp-2">
          {product.title}
        </Link>

        {/* Format selector */}
        <div className="mb-2 mt-auto">
          <FormatSelector
            formats={product.formats}
            selectedFormatId={selectedFormat?.id ?? null}
            onChange={setSelectedFormat}
          />
        </div>

        {/* Quantity + subtotal row */}
        <div className="mb-2 flex items-center justify-between gap-2">
          <QuantityInput
            value={quantity}
            onChange={setQuantity}
            onInputFocusChange={onInnerInputFocusChange}
          />
          <div className="text-right text-sm font-semibold text-gray-700">
            {subtotal > 0 ? `$${formatPrice(subtotal)}` : '\u00A0'}
          </div>
        </div>

        {/* Add to cart button */}
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={quantity === 0 || !selectedFormat || isAdding}
          className={`w-full rounded-lg sm:rounded-md px-3 py-2.5 sm:py-2 text-sm font-medium transition-colors ${
            added
              ? 'bg-green-600 text-white'
              : quantity === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
          }`}
        >
          {isAdding ? (
            <span className="flex items-center justify-center gap-2">
              <i className="fa fa-spinner fa-spin" /> Agregando...
            </span>
          ) : added ? (
            <span className="flex items-center justify-center gap-2">
              <i className="fa fa-check" /> Agregado
            </span>
          ) : quantity === 0 ? (
            'Seleccioná cantidad'
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="fa fa-shopping-cart" /> Agregar al carrito
            </span>
          )}
        </button>

        {/* Warnings */}
        {product.warnings.length > 0 && (
          <div className="mt-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
            {product.warnings[0]}
          </div>
        )}
      </div>
    );
  }
);
